import { drawExtensionIcon } from '@/extensions/pixelIcons';
import { NO_NODE_HANDLE } from '@/models/logic/RuntimeHandles';
import { z } from 'zod';
import type { IUiModel } from '@/interfaces/models';
import { InputType, type TileDefinition } from '@/models/tiles/TileDefinition';
import { configureTile } from '../configuration';
import { CircuitType, Side } from '@/schemas/common';
import { TileSideIOType } from '@/schemas/tiles';
import { standardBuildAndConnect } from '@/models/logic/utils';
import {
  defaultTileGraphics,
  connectionGraphics,
} from '@/models/tiles/DefaultTileGraphics';
import { colors } from '@/styles';
export const WS_BIT = 'extension:digitiles.websocket/bit' as const;
export const WS_READY = 'extension:digitiles.websocket/ready' as const;
export const WS_ADVANCE = 'extension:digitiles.websocket/advance' as const;
export const endpointSchema = z
  .string()
  .trim()
  .max(2048)
  .refine((value) => {
    if (!value) return true;
    try {
      const url = new URL(value);
      return (
        ['ws:', 'wss:'].includes(url.protocol) &&
        !url.username &&
        !url.password &&
        !url.hash
      );
    } catch {
      return false;
    }
  }, 'Enter a ws:// or wss:// URL without credentials or a fragment, or leave blank.');
export const configSchema = z.object({
  endpoint: endpointSchema,
  bit: z.number().int().min(0).max(7),
});
export type Config = z.infer<typeof configSchema>;
export const defaults = (): Config => ({ endpoint: '', bit: 0 });
export const runtimeSchema = configSchema.extend({
  enabled: z.boolean(),
  value: z.boolean(),
});
export type Runtime = z.infer<typeof runtimeSchema>;
export function socketTile(
  type: typeof WS_BIT | typeof WS_READY | typeof WS_ADVANCE,
): TileDefinition<Config> {
  const advance = type === WS_ADVANCE;
  const ioSides = {
    [Side.TOP]: { type: TileSideIOType.NONE, forceConnection: false },
    [Side.RIGHT]: { type: TileSideIOType.OUTPUT, forceConnection: true },
    [Side.BOTTOM]: { type: TileSideIOType.NONE, forceConnection: false },
    [Side.LEFT]: {
      type: advance ? TileSideIOType.INPUT : TileSideIOType.NONE,
      forceConnection: advance,
    },
  };
  const tile: TileDefinition<Config> = {
    type,
    name:
      type === WS_BIT
        ? 'WebSocket byte bit'
        : advance
          ? 'WebSocket advance'
          : 'WebSocket ready',
    canRotate: true,
    stateSchema: configSchema,
    createDefaultState: defaults,
    graphConfiguration: (state) => ({ endpoint: state.endpoint, bit: state.bit }),
    ioSides,
    buildAndConnectNodes(data) {
      const queue = standardBuildAndConnect(type, data, tile);
      const node = data.graph.nodes.get(
        data.instance.logicNodeIds?.top ?? NO_NODE_HANDLE,
      );
      if (node) Object.assign(node.state, data.instance.state);
      return queue;
    },
    createContextMenuItems: ({ instance, chipEditorModel }) => [
      {
        label: `WebSocket URL${instance.state.endpoint ? ' (configured)' : ' (not set)'}`,
        action: async (takeInput) => {
          try {
            const endpoint = await takeInput(
              InputType.STRING,
              'WebSocket URL (ws:// or wss://)',
              instance.state.endpoint,
              true,
              (value) => ({
                isValid: endpointSchema.safeParse(value).success,
                errorMessage:
                  'Enter ws:// or wss:// without credentials or a fragment. Blank disconnects.',
              }),
            );
            configureTile(
              instance,
              chipEditorModel,
              { endpoint: endpointSchema.parse(endpoint) },
              'Change WebSocket URL',
            );
          } catch {
            /* Cancel retains endpoint. */
          }
        },
      },
      ...(type === WS_BIT
        ? [
            {
              label: `Byte bit (${instance.state.bit})`,
              action: async (takeInput: IUiModel['openGenericInputDialog']) => {
                try {
                  const bit = await takeInput(
                    InputType.NUMBER,
                    'Byte bit (0–7; 0 is least significant)',
                    instance.state.bit,
                    true,
                    (value) => ({
                      isValid: configSchema.shape.bit.safeParse(Number(value)).success,
                      errorMessage: 'Enter a whole number from 0 to 7.',
                    }),
                  );
                  configureTile(
                    instance,
                    chipEditorModel,
                    { bit: configSchema.shape.bit.parse(Number(bit)) },
                    'Change WebSocket bit',
                  );
                } catch {
                  /* Cancel retains bit. */
                }
              },
            },
          ]
        : []),
    ],
    graphics: {
      type,
      draw(g, { meta, connections, context }) {
        defaultTileGraphics(g);
        connectionGraphics(
          g,
          colors.signalColor,
          colors.noSignalColor,
          meta,
          connections,
          ioSides,
          context,
        );
        drawExtensionIcon(
          g,
          'socket',
          meta.outputs.right ? 0x83d6b1 : 0x648c7c,
          advance ? 'advance' : type === WS_READY ? 'ready' : 'bit',
        );
      },
    },
  };
  return tile;
}
export const socketContributions = [WS_BIT, WS_READY, WS_ADVANCE].map((type) => ({
  definition: socketTile(type),
  circuits: [CircuitType.ChipLogic],
}));
